import { useMemo } from 'react';
import { useGetNextAppointmentQuery } from '@features/appointments';
import { addDays,isWithinInterval, parseISO, startOfDay } from 'date-fns';

/**
 * Hook to fetch and filter upcoming appointments to only those occurring in the next week.
 * Used across dashboard variants to ensure consistent display logic.
 *
 * @returns {Object} Object containing:
 *   - appointments: Filtered array of appointments (next 7 days)
 *   - isLoading: Loading state from the query
 *   - hasRelevantAppointments: Boolean indicating if any relevant appointments exist
 */
export const useUpcomingRelevantAppointments = () => {
  // Fetch next 3 appointments (already in chronological order)
  const { data, isLoading } = useGetNextAppointmentQuery({ num: 3 });

  // Filter to only appointments in the next 7 days
  const relevantAppointments = useMemo(() => {
    const now = new Date();
    const weekFromNow = addDays(startOfDay(now), 7);

    return data?.items?.filter(appointment => {
      if (!appointment.start_time) return false;
      const date = parseISO(appointment.start_time);
      return isWithinInterval(date, {
        start: now,
        end: weekFromNow
      });
    }) || [];
  }, [data?.items]);

  return {
    appointments: relevantAppointments,
    isLoading,
    hasRelevantAppointments: relevantAppointments.length > 0,
  };
};